<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useDebounceFn, useToggle } from '@vueuse/core';
import getUnixTime from 'date-fns/getUnixTime';
import { useI18n } from 'vue-i18n';

import liveReports from 'dashboard/api/liveReports';
import { useEmitter } from 'dashboard/composables/emitter';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MetricCard from '../overview/MetricCard.vue';
import BaseHeatmap from '../heatmaps/BaseHeatmap.vue';
import HeatmapDateRangeSelector from '../heatmaps/HeatmapDateRangeSelector.vue';

const { t } = useI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const heatmapData = ref([]);
const isLoading = ref(false);
const selectedFrom = ref(null);
const selectedTo = ref(null);
const selectedInboxId = ref(null);
let hasLoaded = false;
let isFetching = false;
let shouldRefetch = false;

const numberOfRows = computed(() => {
  if (!selectedFrom.value || !selectedTo.value) return 0;
  return (
    Math.round(
      (selectedTo.value - selectedFrom.value) / (24 * 60 * 60 * 1000)
    ) + 1
  );
});

const inboxMenuItems = computed(() => [
  {
    label: t('INBOX_REPORTS.ALL_INBOXES'),
    value: null,
  },
  ...inboxes.value.map(inbox => ({ label: inbox.name, value: inbox.id })),
]);

const selectedInboxLabel = computed(
  () =>
    inboxMenuItems.value.find(item => item.value === selectedInboxId.value)
      ?.label || t('INBOX_REPORTS.ALL_INBOXES')
);

const fetchData = async () => {
  if (!selectedFrom.value || !selectedTo.value) return;
  if (isFetching) {
    shouldRefetch = true;
    return;
  }

  isFetching = true;
  isLoading.value = !hasLoaded;
  try {
    const { data } = await liveReports.getVoiceCallHeatmap({
      since: getUnixTime(selectedFrom.value),
      until: getUnixTime(selectedTo.value),
      inbox_id: selectedInboxId.value,
      timezone_offset: -new Date().getTimezoneOffset() / 60,
    });
    heatmapData.value = data;
    hasLoaded = true;
  } finally {
    isFetching = false;
    isLoading.value = false;
    if (shouldRefetch) {
      shouldRefetch = false;
      fetchData();
    }
  }
};

const [showInboxDropdown, toggleInboxDropdown] = useToggle();

const handleInboxAction = ({ value }) => {
  toggleInboxDropdown(false);
  selectedInboxId.value = value;
};

const refreshData = useDebounceFn(fetchData, 300);

useEmitter(BUS_EVENTS.VOICE_CALL_UPDATED, refreshData);
useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, refreshData);

watch([selectedFrom, selectedTo, selectedInboxId], fetchData);

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <MetricCard>
    <template #header>
      <div
        class="col-span-full flex min-w-0 flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between"
      >
        <div class="flex min-w-0 flex-wrap items-center gap-2">
          <h5 class="mb-0 break-words text-lg font-medium text-n-slate-12">
            {{ $t('VOICE_CALL_DASHBOARD.HEATMAP.HEADER') }}
          </h5>
          <span
            class="flex shrink-0 items-center gap-1 rounded bg-n-teal-3 px-2 py-0.5 text-xs text-n-teal-11"
          >
            <span class="size-1 rounded-full bg-n-teal-9" />
            {{ $t('OVERVIEW_REPORTS.LIVE') }}
          </span>
        </div>
        <div class="flex min-w-0 flex-wrap items-center gap-2">
          <HeatmapDateRangeSelector
            v-model:from="selectedFrom"
            v-model:to="selectedTo"
          />
          <div
            v-on-clickaway="() => toggleInboxDropdown(false)"
            class="relative flex min-w-0 items-center"
          >
            <Button
              sm
              slate
              faded
              :label="selectedInboxLabel"
              class="max-w-[200px] rounded-md"
              @click="toggleInboxDropdown()"
            />
            <DropdownMenu
              v-if="showInboxDropdown"
              :menu-items="inboxMenuItems"
              show-search
              :search-placeholder="$t('INBOX_REPORTS.SEARCH_INBOX')"
              class="top-full mt-1 max-h-96 max-w-56 !min-w-56 ltr:right-0 rtl:left-0"
              @action="handleInboxAction($event)"
            />
          </div>
        </div>
      </div>
    </template>
    <p class="mb-3 -mt-1 text-sm text-n-slate-11">
      {{ $t('VOICE_CALL_DASHBOARD.HEATMAP.DESCRIPTION') }}
    </p>
    <BaseHeatmap
      :heatmap-data="heatmapData"
      :number-of-rows="numberOfRows"
      :is-loading="isLoading"
    />
  </MetricCard>
</template>
